import lodashMemoize from "lodash/memoize";
import { ValidationErrorMessage } from "ra-core";

// NOTE: Copied over from ra-core validation, to memoize the validator which get re-use all the time

// Extend ra-core validation to treat empty spaces as empty
const isEmpty = (value: any) =>
  typeof value === "undefined" ||
  value === null ||
  value === "" ||
  (typeof value === "string" &&
    value.replace(/<\/?[^>]+(>|$)/gi, "").trim() === "") ||
  (Array.isArray(value) && value.length === 0);
type Memoize = <T extends (...args: any[]) => any>(
  func: T,
  resolver?: (...args: any[]) => any
) => T;

interface MessageFuncParams {
  args: any;
  value: any;
  values: any;
}

type MessageFunc = (params: MessageFuncParams) => ValidationErrorMessage;

export const getMessage = (
  message: string | MessageFunc,
  messageArgs: any,
  value: any,
  values: any
) =>
  typeof message === "function"
    ? message({
        args: messageArgs,
        value,
        values,
      })
    : messageArgs
    ? {
        message,
        args: messageArgs,
      }
    : message;

// If we define validation functions directly in JSX, it will
// result in a new function at every render, and then trigger infinite re-render.
// Hence, we memoize every built-in validator to prevent a "Maximum call stack" error.
export const memoize: Memoize = (fn: any) =>
  lodashMemoize(fn, (...args) => JSON.stringify(args));

/**
 * Equal value validator
 *
 * Returns an error if the value is not equal to the expected value
 *
 * @param {number | string} expected
 * @param {string|Function} message
 *
 * @example
 *
 * const answerValidators = [isEqualTo(10, 'Must be 10')];
 * <TextInput type="number" name="answer" validate={answerValidators} />
 */
export const isEqualTo = memoize(
  (expected, message = "portal.validation.isEqualTo") => (
    value,
    values = null
  ) =>
    // eslint-disable-next-line eqeqeq
    !isEmpty(value) && value != expected // use eqeq for flexible type comparison, e.g. expected number and input value is string
      ? getMessage(message, { expected }, value, values)
      : undefined
);

/**
 * Required validator
 *
 * Returns an error if the value is null, undefined, or empty
 *
 * @param {string|Function} message
 *
 * @example
 *
 * const titleValidators = [required('The title is required')];
 * <TextInput name="title" validate={titleValidators} />
 */
export const required = memoize((message = "ra.validation.required") =>
  Object.assign(
    (value, values) =>
      isEmpty(value)
        ? getMessage(message, undefined, value, values)
        : undefined,
    { isRequired: true }
  )
);
